import type { AccountState, AccountView } from "../access/api";
import type { AccessFailure } from "../access/failure";

/**
 * Pure presentation logic of the account administration ([F-028]): which
 * actions a row offers, the list filter, the role choice rule, and how a
 * refused change is answered. The server owns every rule; these helpers only
 * shape what the screen offers and how it reacts.
 */

export interface Viewer {
  /** The viewing account's id - its own row offers no "Stilllegen". */
  selfId: string | null;
  /** The system account ([F-031], [F-035]): it alone recovers an address. */
  isSystem: boolean;
}

export interface AccountActions {
  editRoles: boolean;
  resendInvite: boolean;
  retire: boolean;
  reactivate: boolean;
  sessions: boolean;
  recover: boolean;
}

/**
 * What a row offers.
 *
 *   - roles: on every account not retired ([F-026] - at least one, several);
 *   - the invitation again: while the account waits for its first login
 *     ([F-029] - the old link dies with it);
 *   - retire / reactivate: by state ([F-028] - accounts are retired, never
 *     deleted); not on the own row, where one click would end the work;
 *   - sessions: always ([NF-008], [AC-063]);
 *   - recover the address: the system account only ([F-035]).
 */
export function accountActions(account: AccountView, viewer: Viewer): AccountActions {
  const state: AccountState = account.state ?? "active";
  const own = viewer.selfId !== null && viewer.selfId === account.id;
  return {
    editRoles: state !== "retired",
    resendInvite: state === "unconfirmed",
    retire: state !== "retired" && !own,
    reactivate: state === "retired",
    sessions: true,
    recover: viewer.isSystem && state !== "retired",
  };
}

/** i18n keys of the three states ([DO-012]). */
export const STATE_LABEL_KEY: Record<AccountState, string> = {
  unconfirmed: "adminUsers.states.unconfirmed",
  active: "adminUsers.states.active",
  retired: "adminUsers.states.retired",
};

/**
 * The list as shown: retired accounts only on request (the administration
 * sees them, [F-030]); the search matches display name and mail address,
 * ignoring case.
 */
export function visibleAccounts(list: readonly AccountView[], query: string, showRetired: boolean): AccountView[] {
  const needle = query.trim().toLowerCase();
  return list.filter((account) => {
    if (!showRetired && account.state === "retired") return false;
    if (!needle) return true;
    return (
      account.display_name.toLowerCase().includes(needle) || (account.email ?? "").toLowerCase().includes(needle)
    );
  });
}

/** [F-026] / [AC-046]: every control that gives roles allows several and leaves none without. */
export function rolesChoiceValid(roles: readonly string[]): boolean {
  return roles.length > 0;
}

/** Toggle one role in a choice, keeping the given order. */
export function toggleRole(roles: readonly string[], role: string, order: readonly string[]): string[] {
  const next = roles.includes(role) ? roles.filter((r) => r !== role) : [...roles, role];
  return order.filter((r) => next.includes(r));
}

/**
 * How the screen answers a refused account change:
 *
 *   - last-admin-code: the change would leave no active administrator; a code
 *     went to the system account and the change can be repeated with it
 *     ([F-032], [AC-057]);
 *   - guard: a domain rule refused it - the last administrator without a
 *     system account, a race, an Owner of open incidents losing both party
 *     roles (`error.owner_role_protected`) - shown as the server's sentence in
 *     a dialog that has to be acknowledged;
 *   - error: anything else, as a toast.
 *
 * A refusal for missing elevation never reaches here: `withElevation` turns
 * it into the step-up dialog and repeats the change.
 */
export type ChangeOutcome = "last-admin-code" | "guard" | "error";

export function changeOutcome(failure: AccessFailure): ChangeOutcome {
  if (failure.requires === "last-admin-code") return "last-admin-code";
  if (failure.status === 409 || failure.status === 422) return "guard";
  return "error";
}
